import type { layout } from "@app/domain";
import type { DealRun } from "./useDeal";

/**
 * The deal's motion (handoff "Patlama"), as Web Animations on the elements the
 * `Deal` layer draws. Times and curves are the handoff's; no blur anywhere and the
 * glow only in the first half of a flight (owner, 11 Sept, performance).
 */

type Add = (el: Element | null, frames: Keyframe[], options: KeyframeAnimationOptions) => void;

const GOLD = "rgb(253 230 138";
const FLIGHT_EASE = "cubic-bezier(.15,.85,.25,1)";

function flightFrames(f: layout.DealFlight): Keyframe[] {
  const mine = f.handIndex !== null;
  return [
    {
      transform: "translate(0px, 0px) rotate(0deg) scale(0.5) translateZ(40px)",
      opacity: 1,
      filter: `drop-shadow(0 0 10px ${GOLD}))`,
    },
    {
      offset: 0.55,
      transform: `translate(${f.mid.x}px, ${f.mid.y}px) rotate(${f.midRotate}deg) scale(${f.midScale}) translateZ(160px)`,
      filter: `drop-shadow(0 0 8px ${GOLD} / 0.9))`,
    },
    {
      transform: `translate(${f.to.x}px, ${f.to.y}px) rotate(${f.endRotate}deg) scale(${f.endScale}) translateZ(0px)`,
      opacity: mine ? 1 : 0,
      filter: "none",
    },
  ];
}

/** The deck charges and bursts, the flash and the ring spread, the stage shakes once. */
function burst(root: HTMLElement, stage: Element | null, run: DealRun, add: Add): void {
  const { charge, step, fly } = run.timing;
  for (const layer of root.querySelectorAll('[data-deal="deck"]'))
    add(
      layer,
      [
        { transform: "scale(1)", filter: "none", opacity: 1 },
        {
          offset: 0.8,
          transform: "scale(0.9) translateZ(30px)",
          filter: `brightness(1.8) drop-shadow(0 0 20px ${GOLD} / 0.9))`,
        },
        { transform: "scale(1.3)", filter: "brightness(3)", opacity: 0 },
      ],
      { duration: charge, easing: "cubic-bezier(.6,0,.9,.4)", fill: "forwards" },
    );
  add(
    root.querySelector('[data-deal="flash"]'),
    [
      { opacity: 0, transform: "scale(1)" },
      { offset: 0.3, opacity: 1, transform: "scale(1.6)" },
      { opacity: 0, transform: "scale(2.4)" },
    ],
    { delay: charge - 60, duration: 600, easing: "ease-out", fill: "forwards" },
  );
  add(
    root.querySelector('[data-deal="ring"]'),
    [
      { opacity: 1, transform: "scale(0.1)" },
      { opacity: 0, transform: "scale(4)" },
    ],
    { delay: charge - 40, duration: 700, easing: "cubic-bezier(.1,.8,.3,1)", fill: "forwards" },
  );
  add(
    stage,
    [
      { scale: "1", translate: "0px 0px" },
      { offset: 0.3, scale: "1.05", translate: "0px 0px" },
      { offset: 0.36, scale: "1.02", translate: "-6px 4px" },
      { offset: 0.42, scale: "1.02", translate: "6px -4px" },
      { offset: 0.48, scale: "1.02", translate: "-3px 2px" },
      { scale: "1", translate: "0px 0px" },
    ],
    { duration: charge + fly + 12 * step + 300, easing: "ease-out" },
  );
}

/** A ring flares around each pod as its first card arrives, and the pod jumps a little. */
function impacts(root: HTMLElement, stage: Element | null, run: DealRun, add: Add): void {
  const arrive = run.timing.charge + run.timing.fly;
  for (const slot of ["seatTop", "seatLeft", "seatRight", "seatSelf"]) {
    add(
      root.querySelector(`[data-deal="impact"][data-slot="${slot}"]`),
      [
        { opacity: 0, transform: "scale(0.85)" },
        { offset: 0.2, opacity: 1, transform: "scale(1.05)" },
        { opacity: 0, transform: "scale(1.6)" },
      ],
      { delay: arrive - 60, duration: 700, easing: "ease-out", fill: "forwards" },
    );
    add(
      stage?.querySelector(`[data-pod="${slot}"] [data-circle]`) ?? null,
      [{ scale: "1" }, { offset: 0.3, scale: "1.18" }, { scale: "1" }],
      { delay: arrive - 30, duration: 500 },
    );
  }
}

/** Every card: the flight, and for yours the turn face up on the way. */
function flights(root: HTMLElement, run: DealRun, add: Add, calm: boolean): void {
  for (const f of run.flights) {
    const el = root.querySelector(`[data-id="${f.id}"]`);
    if (calm) {
      // Reduced motion: your cards simply appear where they belong; nothing flies.
      if (f.handIndex === null) continue;
      const at = `translate(${f.to.x}px, ${f.to.y}px) scale(${f.endScale})`;
      add(
        el,
        [
          { opacity: 0, transform: at },
          { opacity: 1, transform: at },
        ],
        {
          duration: 250,
          fill: "forwards",
        },
      );
      add(el?.firstElementChild ?? null, [{ transform: "rotateY(180deg)" }], {
        duration: 0,
        fill: "forwards",
      });
      continue;
    }
    add(el, flightFrames(f), {
      delay: f.delay,
      duration: run.timing.fly,
      easing: FLIGHT_EASE,
      fill: "forwards",
    });
    if (f.handIndex !== null)
      add(
        el?.firstElementChild ?? null,
        [{ transform: "rotateY(0deg)" }, { transform: "rotateY(180deg)" }],
        {
          delay: f.delay + run.timing.fly * 0.35,
          duration: run.timing.fly * 0.6,
          easing: "ease-out",
          fill: "forwards",
        },
      );
  }
}

/** Runs a deal on the layer under `root`; returns the cleanup that cancels it. */
export function playDeal(root: HTMLElement | null, run: DealRun, onDone: () => void): () => void {
  // No layer to draw on: the deal still ends, or the hand would stay hidden for good (0027 C9).
  if (root === null) {
    onDone();
    return () => undefined;
  }
  const calm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const stage = root.closest("[data-scene]");
  const running: Animation[] = [];
  const add: Add = (el, frames, options) => {
    if (el !== null) running.push(el.animate(frames, options));
  };
  if (!calm && run.timing.charge > 0) {
    burst(root, stage, run, add);
    impacts(root, stage, run, add);
  }
  flights(root, run, add, calm);
  const timer = window.setTimeout(onDone, calm ? 250 : run.duration);
  return () => {
    window.clearTimeout(timer);
    for (const animation of running) animation.cancel();
  };
}
